import React, { useMemo, useRef } from "react";
import { Card, CircularProgress } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import CallMadeIcon from "@mui/icons-material/CallMade";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import Box from "@mui/material/Box";

ChartJS.register(...registerables);

const DashboardCard = ({
  color,
  colorcode,
  title,
  count,
  data1,
  labels,
  icon,
  labelicon,
  imgicon,
  selectedCardCode,
  iconBg,
  loading,
  isSelected,
  driverCount,
  darkMode,
  titleFontSize,
  driverFontSize,
}) => {
  const chartRef = useRef(null);

  const hexToRgba = (hex, opacity) => {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
      hex = hex.split("").map((c) => c + c).join("");
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const gradientBg = useMemo(
    () => (context) => {
      const ctx = context.chart.ctx;
      if (!ctx) return null;
      const gradient = ctx.createLinearGradient(0, 0, 0, 100);
      gradient.addColorStop(1, "#ffffff");
      gradient.addColorStop(0, hexToRgba(colorcode, 0.3));
      return gradient;
    },
    [colorcode]
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: data1 || [],
          borderColor: colorcode,
          backgroundColor: gradientBg,
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        },
      ],
    }),
    [labels, data1, colorcode, gradientBg]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: { display: false },
        y: { display: false },
      },
      elements: {
        point: { radius: 0 },
      },
      plugins: {
        legend: { display: false },
      },
    }),
    []
  );

  return (
    <Card
      sx={{
        backgroundColor: isSelected
          ? selectedCardCode
          : darkMode
            ? "#202940"
            : "#fff",
        boxShadow: "4px 6px 15px rgba(0,0,0,0.16)",
        cursor: "pointer",
      }}
    >
      {/* HEADER */}
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        {/* ICON */}
        <MDBox
          variant={iconBg ? undefined : "gradient"}
          bgColor={iconBg ? undefined : color}
          sx={iconBg ? { background: iconBg } : {}}
          color={color === "light" ? "dark" : "white"}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          {labelicon ? (
            React.createElement(labelicon, { style: { width: 32, height: 32 } })
          ) : imgicon ? (
            <img src={imgicon} alt={title} style={{ width: "2rem" }} />
          ) : (
            <Icon fontSize="large">{icon}</Icon>
          )}
        </MDBox>

        {/* TITLE + VALUE */}
        <MDBox textAlign="right">
          <MDTypography
            variant="button"
            fontWeight="light"
            style={{
              color: isSelected ? colorcode : undefined,
              fontSize: titleFontSize || "0.875rem", // default MUI button size
            }}
          >
            {title} {!isSelected ? <CallMadeIcon /> : <SouthEastIcon />}
          </MDTypography>

          {/* COUNT / LOADER */}
          <Box minHeight={56} display="flex" flexDirection="column" alignItems="flex-end">
            {!loading && (
              <>
                <MDTypography
                  variant="h4"
                  style={{ color: isSelected ? colorcode : undefined }}
                >
                  {count}
                </MDTypography>

                <MDTypography
                  variant="h6"
                  style={{
                    fontSize: driverFontSize || "1.25rem", // default h6 size
                  }}
                >
                  {driverCount}
                </MDTypography>
              </>
            )}
          </Box>

        </MDBox>
      </MDBox>

      {/* CHART / LOADER */}
      <Box
        height={90}
        px={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {loading ? (
          <CircularProgress size={32} sx={{ color: colorcode }} />
        ) : (
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        )}
      </Box>
    </Card>
  );
};

export default React.memo(DashboardCard);
